import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';

export interface CountryPhoneCode {
    name: string;
    name_vi: string;
    code: string;
    phone_code: string;
}

export interface PhoneFormatPattern {
    code: string;
    name: string;
    name_vi: string;
    pattern: RegExp;
    format: string;
    localPattern?: RegExp;
    localFormat?: string;
}

@Injectable({
    providedIn: 'root'
})
export class CountryPhoneCodeService {
    private countryCodes$ = new BehaviorSubject<CountryPhoneCode[]>([]);
    private formatPatterns$ = new BehaviorSubject<PhoneFormatPattern[]>([]);
    private isLoaded = false;

    constructor(private http: HttpClient) {
        // Only load defaults first, full data will be loaded via APP_INITIALIZER
        this.preloadDefaultPatterns();
    }

    // Method for APP_INITIALIZER to load data synchronously
    initializeCountryCodes(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this.isLoaded) {
                resolve();
                return;
            }

            this.http.get<CountryPhoneCode[]>('/assets/international_phone_number_code.json')
                .pipe(
                    map(codes => this.filterAndProcessCodes(codes)),
                    tap(() => this.isLoaded = true),
                    catchError(error => {
                        console.error('Failed to load country codes:', error);
                        // Still resolve to allow app to start with default patterns
                        resolve();
                        return of([]);
                    })
                )
                .subscribe({
                    next: (codes) => {
                        this.countryCodes$.next(codes);
                        this.formatPatterns$.next(this.generateFormatPatterns(codes));
                        resolve();
                    },
                    error: (error) => {
                        console.error('Error loading country codes:', error);
                        resolve(); // Still resolve to allow app to start
                    }
                });
        });
    }

    private preloadDefaultPatterns(): void {
        // Preload essential patterns for immediate use
        const defaultPatterns: PhoneFormatPattern[] = [
            // Vietnam mobile
            {
                code: '84',
                name: 'Vietnam',
                name_vi: 'Việt Nam',
                pattern: /^84(\d{3})(\d{3})(\d{3})$/,
                format: '+84 $1 $2 $3',
                localPattern: /^0(\d{3})(\d{3})(\d{3})$/,
                localFormat: '0$1 $2 $3'
            },
            // Vietnam landline
            {
                code: '84',
                name: 'Vietnam',
                name_vi: 'Việt Nam',
                pattern: /^84(\d{1})(\d{4})(\d{4})$/,
                format: '+84 $1 $2 $3',
                localPattern: /^0(\d{1})(\d{4})(\d{4})$/,
                localFormat: '0$1 $2 $3'
            },
            // USA/Canada
            {
                code: '1',
                name: 'United States',
                name_vi: 'Hoa Kỳ',
                pattern: /^1(\d{3})(\d{3})(\d{4})$/,
                format: '+1 ($1) $2-$3',
                localPattern: /^(\d{3})(\d{3})(\d{4})$/,
                localFormat: '($1) $2-$3'
            },
            // Philippines
            {
                code: '63',
                name: 'Philippines',
                name_vi: 'Philippines',
                pattern: /^63(\d{3})(\d{3})(\d{4})$/,
                format: '+63 $1 $2 $3',
                localPattern: /^0(\d{3})(\d{3})(\d{4})$/,
                localFormat: '0$1 $2 $3'
            }
        ];

        this.formatPatterns$.next(defaultPatterns);
    }


    private filterAndProcessCodes(codes: CountryPhoneCode[]): CountryPhoneCode[] {
        return codes.filter(country => {
            // Filter out +1-xxx cases to avoid conflict with main +1 (US/Canada)
            return !country.phone_code.match(/^\+1-\d+$/);
        });
    }

    private generateFormatPatterns(codes: CountryPhoneCode[]): PhoneFormatPattern[] {
        const patterns: PhoneFormatPattern[] = [];

        codes.forEach(country => {
            const phoneCode = country.phone_code.replace('+', '');
            const codeLength = phoneCode.length;

            // Generate pattern based on phone code and common number formats
            let pattern: RegExp;
            let format: string;
            let localPattern: RegExp | undefined;
            let localFormat: string | undefined;


            // Generic formatting for other countries
            if (codeLength <= 2) {
                pattern = new RegExp(`^${phoneCode}(\\d{2,4})(\\d{3,4})(\\d{3,4})$`);
                format = `+${phoneCode} $1 $2 $3`;
            } else if (codeLength === 3) {
                pattern = new RegExp(`^${phoneCode}(\\d{2,3})(\\d{3,4})(\\d{3,4})$`);
                format = `+${phoneCode} $1 $2 $3`;
            } else {
                pattern = new RegExp(`^${phoneCode}(\\d{1,3})(\\d{3,4})(\\d{3,4})$`);
                format = `+${phoneCode} $1 $2 $3`;
            }

            // Add main pattern
            patterns.push({
                code: phoneCode,
                name: country.name,
                name_vi: country.name_vi,
                pattern,
                format,
                localPattern,
                localFormat
            });
        });

        return patterns.sort((a, b) => a.code.length - b.code.length);
    }

    getCountryCodes(): Observable<CountryPhoneCode[]> {
        return this.countryCodes$.asObservable();
    }

    getFormatPatterns(): Observable<PhoneFormatPattern[]> {
        return this.formatPatterns$.asObservable();
    }

    getCurrentFormatPatterns(): PhoneFormatPattern[] {
        return this.formatPatterns$.value;
    }

    getCountryByPhoneCode(phoneCode: string): CountryPhoneCode | null {
        const codes = this.countryCodes$.value;
        const cleanCode = phoneCode.replace(/^\+/, '');

        return codes.find(country =>
            country.phone_code === `+${cleanCode}` ||
            country.phone_code === phoneCode
        ) || null;
    }
}
